import { useMemo } from "react";
import type { ThemeClasses, TrackingReplay } from "../types";

interface TrackingReplayGridCardProps {
  replay: TrackingReplay;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  onView: (replay: TrackingReplay) => void;
  onDelete: (replay: TrackingReplay) => void;
  isDeleting?: boolean;
}

function formatDuration(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffMs = endDate.getTime() - startDate.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function TrackingReplayGridCard({
  replay,
  themeClasses,
  onView,
  onDelete,
  isDeleting,
}: TrackingReplayGridCardProps) {
  const duration = useMemo(
    () => formatDuration(replay.start_date, replay.end_date),
    [replay.start_date, replay.end_date]
  );

  const positionCount = replay.positions?.length || 0;

  return (
    <div
      className={`${themeClasses.cardBg} border ${themeClasses.cardBorder} rounded-lg p-4 hover:shadow-md transition-shadow`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
            <i className="ri-route-line text-primary-600 text-lg"></i>
          </div>
          <div>
            <h3 className={`font-semibold ${themeClasses.text} truncate max-w-[180px]`}>
              {replay.name}
            </h3>
            <p className={`text-sm ${themeClasses.textSecondary}`}>
              {positionCount} puntos
            </p>
          </div>
        </div>
      </div>

      <div className={`space-y-2 text-sm mb-4 ${themeClasses.textSecondary}`}>
        <div className="flex justify-between">
          <span>Duración:</span>
          <span className={themeClasses.text}>{duration}</span>
        </div>
        <div className="flex justify-between">
          <span>Vehículo:</span>
          <span className={`${themeClasses.text} truncate max-w-[120px]`} title={replay.vehicle_id}>
            {replay.vehicle_id.slice(0, 8)}...
          </span>
        </div>
        <div className="flex justify-between">
          <span>Inicio:</span>
          <span className={themeClasses.text}>
            {formatDate(replay.start_date)}
          </span>
        </div>
        <div className="flex justify-between">
          <span>Fin:</span>
          <span className={themeClasses.text}>
            {formatDate(replay.end_date)}
          </span>
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onView(replay)}
          className="flex-1 px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
        >
          <i className="ri-eye-line mr-1"></i>
          Ver
        </button>
        <button
          onClick={() => onDelete(replay)}
          disabled={isDeleting}
          className="px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium disabled:opacity-50"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      </div>
    </div>
  );
}
